import React from 'react';
import Slider from 'react-slick';
import useMediaQuery from '@mui/material/useMediaQuery';
import {useTheme, Box, PaletteMode} from '@mui/material';
import {useSwipeable} from 'react-swipeable';
import SearchToursCard from './SearchToursCard';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {IconButton} from '@mui/material';
import {ArrowBackIos, ArrowForwardIos} from '@mui/icons-material';
import Typography from "@mui/material/Typography";
import {alpha} from "@mui/material/styles";


interface CardData {
    title: string;
    description?: string;
    regionId: number;
    compoundTypes: string[];
    weekendOffset?: number;
    curationId?: number;
    imageUrl: string;
    showCurator: boolean;
    curatorImageUrl?: string;
}

interface GridProps {
    keyword: string;
    title: string;
    title_emph: string;
    description?: string;
    cards: CardData[];
}

const NextArrow = (props: any) => {
    const {onClick} = props;
    return (
        <IconButton
            onClick={onClick}
            sx={{
                position: 'absolute',
                top: '50%',
                right: -10,
                zIndex: 1,
                transform: 'translateY(-50%)',
                backgroundColor: 'primary',
                boxShadow: 2,
                '&:hover': {backgroundColor: 'grey.200'},
            }}
        >
            <ArrowForwardIos/>
        </IconButton>
    );
};

const PrevArrow = (props: any) => {
    const {onClick} = props;
    return (
        <IconButton
            onClick={onClick}
            sx={{
                position: 'absolute',
                top: '50%',
                left: -10,
                zIndex: 1,
                transform: 'translateY(-50%)',
                backgroundColor: 'primary',
                boxShadow: 2,
                '&:hover': {backgroundColor: 'grey.200'},
            }}
        >
            <ArrowBackIos/>
        </IconButton>
    );
};




const SearchToursCardGrid: React.FC<GridProps> = ({keyword, title, title_emph, description, cards}) => {
    const theme = useTheme();
    const mode = theme.palette.mode;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [activeIndex, setActiveIndex] = React.useState(0);

    const handleSwipeRight = () => {
        const card = cards[activeIndex];
        document.getElementById(`card-button-${activeIndex}`)?.click();
    };

    const handleSwipeLeft = () => {
        setActiveIndex((prev) => (prev + 1) % cards.length);
    };

    const handlers = useSwipeable({
        onSwipedLeft: handleSwipeLeft,
        onSwipedRight: handleSwipeRight,
        trackMouse: true,
        // Standardverhalten (z.B. Scrollen) verhindern:
        // onTouchStartOrOnMouseDown: (e) => e.preventDefault(),
    });

    if (cards.length === 0) {
        return null;
    }

    if (isMobile) {
        // Mobile: Tinder-Style Swiper
        const card = cards[activeIndex];
        return (
            <Box {...handlers} sx={{width: '100%', display: 'flex', justifyContent: 'center'}}>
                <Box maxWidth={350}>
                    <SearchToursCard
                        {...card}
                        buttonId={`card-button-${activeIndex}`} // Damit der Button klickbar bleibt
                    />
                </Box>
            </Box>
        );
    }

    const desktopCardLimit = 3;
    const useSlider = cards.length > desktopCardLimit;

    // Desktop: Carousel (react-slick)
    const settings = {
        dots: useSlider,
        infinite: useSlider,
        speed: 500,
        centerMode: false,
        slidesToShow: desktopCardLimit,
        slidesToScroll: 1,
        nextArrow: useSlider ? <NextArrow/> : undefined,
        prevArrow: useSlider ? <PrevArrow/> : undefined,
        responsive: [
            {
                breakpoint: 960,
                settings: {
                    slidesToShow: 2,
                },
            },
            {
                breakpoint: 600,
                settings: {
                    slidesToShow: 1,
                },
            },
        ],
    };

    return (
        <Box data-aos={isMobile ? 'fade-right' : 'fade-up'}>
            {keyword && title && title_emph && (
                <React.Fragment>
                    <Typography
                        sx={{
                            textTransform: 'uppercase',
                            fontWeight: 'medium',
                        }}
                        gutterBottom
                        color={'text.secondary'}
                    >
                        {keyword}
                    </Typography>
                    <Box marginBottom={2}>
                        <Typography
                            variant="h3"
                            color="text.primary"
                            sx={{
                                fontWeight: 700,
                            }}
                        >
                            {title}
                            <Typography
                                color={'text.primary'}
                                component={'span'}
                                variant={'inherit'}
                                sx={{
                                    background: `linear-gradient(180deg, transparent 82%, ${alpha(
                                        mode === 'light' as PaletteMode ? theme.palette.secondary.main : theme.palette.primary.main,
                                        0.3,
                                    )} 0%)`,
                                }}
                            >
                                {title_emph}
                            </Typography>
                        </Typography>
                    </Box>
                </React.Fragment>
            )}

            {description && (
                <Box marginBottom={3}>
                    <Typography variant="h6" component="p" color="text.secondary">
                        {description}
                    </Typography>
                </Box>
            )}

            <Box sx={{px: 0}}>
                {useSlider ? (
                    <Slider {...settings}>
                        {cards.map((card, index) => (
                            <Box key={index} px={1} sx={{margin: 5, height: '100%'}}>
                                <SearchToursCard {...card} buttonId={`card-button-${index}`}/>
                            </Box>
                        ))}
                    </Slider>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${cards.length}, minmax(0, 1fr))`, gap: 2 }}>
                        {cards.map((card, index) => (
                            <Box key={index} sx={{ height: '100%' }}>
                                <SearchToursCard {...card} buttonId={`card-button-${index}`}/>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default SearchToursCardGrid;
